// import type { any } from './xdmSchema'
import * as v from 'valibot';
import { ContentDetailsItemCabs, FiltersCabs } from './xdmSchema';
import { ErrorDetailsListItem, Filters } from '@gt-common/event-logger-web/dist/src/schemas/baseSchema';
import { ProductListItemCabs } from './xdmSchema';

export interface AdobeKeyConfig {
  keyPath: string;
  transformer: (data: any) => string | number;
  adobeKeyName: string;
}

const generateComponents = (components: v.InferInput<typeof ContentDetailsItemCabs>[]) => {
  return components
    .map((component, index) => {
      const separator = index === components.length - 1 ? '' : '|';
      return `${component.name}:${component.type}:V_${component.position.v}:H_${component.position.h}${separator}`;
    })
    .join('');
};

const generateProducts = (productList: v.InferInput<typeof ProductListItemCabs>[]) => {
  if (!productList || productList.length === 0) {
    return '';
  }

  return productList
    .map((product) => {
      const productName = product.name?.replace(',', '') || '';
      const supplierName = product.supplier_name || '';
      const fuelType = (product.fuel_type || '').replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, '');
      const sittingCapacity = product.sitting_capacity || '';
      const rating = product.rating || '';

      // Build eVar strings
      const eVar84 = `eVar84=${supplierName}`;
      const eVar85 = `eVar85=${fuelType}:${sittingCapacity}`;
      const eVar112 = `eVar112=${generateCoupons(productList)}`;
      const eVar113 = `eVar113=${rating}`;

      // Combine all parts
      return `;${productName};;;;${eVar84}|${eVar85}|${eVar113}|${eVar112}`;
    })
    .join(',');
};

const generateCabstops = (stops: { location: { name: string } }[] | null) => {
  if (!stops || stops.length === 0) {
    return '';
  }
  return stops.map((stop) => stop.location.name).join(':');
};

const generateFilters = (filters: v.InferInput<typeof FiltersCabs>) => {
  return (
    filters.applied?.map((filter) => `${filter.source || ''}:${filter.category || ''}:${filter.group || ''}:${filter.value || ''}`).join('|') || ''
  );
};

const generateCoupons = (productList: v.InferInput<typeof ProductListItemCabs>[]) => {
  return productList
    ?.map((product: v.InferInput<typeof ProductListItemCabs>) => {
      if (product.price?.discounts?.applicable_coupons) {
        return product.price.discounts.applicable_coupons.map((coupon) => `${coupon.code}:${coupon.is_applied}`).join(':');
      }
      return '';
    })
    .filter(Boolean)
    .join('|');
};

const generateSearchResultsCount = (productList: v.InferInput<typeof ProductListItemCabs>[]) => {
  return productList?.length;
};

const generateErrorDetails = (errorDetails: v.InferInput<typeof ErrorDetailsListItem>[]) => {
  return errorDetails
    ?.map((errorDetail) => {
      return `${errorDetail.code}:${errorDetail.message}`;
    })
    .join('|');
};

export const keysToGenerate: AdobeKeyConfig[] = [
  {
    keyPath: 'event_detail.components.content_details',
    transformer: generateComponents,
    adobeKeyName: 'components_l1',
  },
  {
    keyPath: 'event_detail.components.product_list',
    transformer: generateProducts,
    adobeKeyName: 'products',
  },
  {
    keyPath: 'event_detail.components.product_list',
    transformer: generateSearchResultsCount,
    adobeKeyName: 'search_results_count_e2',
  },
  {
    keyPath: 'search_context.stops',
    transformer: generateCabstops,
    adobeKeyName: 'cabstop_v106',
  },
  {
    keyPath: 'event_detail.components.filters',
    transformer: generateFilters,
    adobeKeyName: 'filters_l3',
  },
  {
    keyPath: 'error_details_list',
    transformer: generateErrorDetails,
    adobeKeyName: 'error_details_v11',
  },
];